<script setup>
import { useAlert } from 'dashboard/composables';
import { computed, onMounted, ref } from 'vue';
import Avatar from 'next/avatar/Avatar.vue';
import { useI18n } from 'vue-i18n';
import { picoSearch } from '@scmmishra/pico-search';
import {
  useStoreGetters,
  useStore,
  useMapGetter,
} from 'dashboard/composables/store';

import AddAgent from './AddAgent.vue';
import EditAgent from './EditAgent.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import SidebarActionsHeader from 'dashboard/components-next/SidebarActionsHeader.vue';
import { useRouter } from 'vue-router';
import { useTeletelPageTabs } from 'dashboard/composables/useTeletelPageTabs';

const router = useRouter();
// Live presence lives on its own page; link to it from the agent list.
const { canViewTeamPresence } = useTeletelPageTabs();
const openTeamPresence = () => router.push({ name: 'teletel_team_presence' });

const getters = useStoreGetters();
const store = useStore();
const { t } = useI18n();

const loading = ref({});
const showAddPopup = ref(false);
const showDeletePopup = ref(false);
const showEditPopup = ref(false);
const agentAPI = ref({ message: '' });
const currentAgent = ref({});
const searchQuery = ref('');

const deleteConfirmText = computed(
  () => `${t('AGENT_MGMT.DELETE.CONFIRM.YES')} ${currentAgent.value.name}`
);
const deleteRejectText = computed(() => {
  return `${t('AGENT_MGMT.DELETE.CONFIRM.NO')} ${currentAgent.value.name}`;
});
const deleteMessage = computed(() => {
  return ` ${currentAgent.value.name}?`;
});

const agentList = computed(() => getters['agents/getAgents'].value);

const uiFlags = computed(() => getters['agents/getUIFlags'].value);
const currentUserId = computed(() => getters.getCurrentUserID.value);
const customRoles = useMapGetter('customRole/getCustomRoles');
const inboxes = useMapGetter('inboxes/getInboxes');
const teams = useMapGetter('teams/getTeams');

// Owner order 2026-09-16: inbox/team chips on every row, filters by role,
// availability and inbox, and a side panel instead of the edit modal.
const roleFilter = ref('');
const statusFilter = ref('');
const inboxFilter = ref('');
const selectedAgentId = ref(null);

const inboxName = id => inboxes.value.find(inbox => inbox.id === id)?.name;
const teamName = id => teams.value.find(team => team.id === id)?.name;
const agentInboxes = agent =>
  (agent.inbox_ids || []).map(inboxName).filter(Boolean);
const agentTeams = agent =>
  (agent.team_ids || []).map(teamName).filter(Boolean);

const roleOptions = computed(() => [
  { value: 'administrator', label: t('AGENT_MGMT.AGENT_TYPES.ADMINISTRATOR') },
  { value: 'agent', label: t('AGENT_MGMT.AGENT_TYPES.AGENT') },
  ...customRoles.value.map(role => ({
    value: `custom:${role.id}`,
    label: role.name,
  })),
]);
const statusOptions = computed(() =>
  ['online', 'busy', 'offline'].map(value => ({
    value,
    label: t(`AGENT_MGMT.FILTERS.STATUS_${value.toUpperCase()}`),
  }))
);
const matchesRole = agent => {
  if (!roleFilter.value) return true;
  if (roleFilter.value.startsWith('custom:')) {
    return `custom:${agent.custom_role_id}` === roleFilter.value;
  }
  return !agent.custom_role_id && agent.role === roleFilter.value;
};
const matchesStatus = agent =>
  !statusFilter.value || agent.availability_status === statusFilter.value;
const matchesInbox = agent =>
  !inboxFilter.value ||
  (agent.inbox_ids || []).includes(Number(inboxFilter.value));
const hasActiveFilters = computed(() =>
  Boolean(roleFilter.value || statusFilter.value || inboxFilter.value)
);
const clearFilters = () => {
  roleFilter.value = '';
  statusFilter.value = '';
  inboxFilter.value = '';
};

const filteredAgentList = computed(() => {
  const query = searchQuery.value.trim();
  const base = query
    ? picoSearch(agentList.value, query, ['name', 'email'])
    : agentList.value;
  return base.filter(
    agent => matchesRole(agent) && matchesStatus(agent) && matchesInbox(agent)
  );
});

const selectedAgent = computed(() =>
  agentList.value.find(agent => agent.id === selectedAgentId.value)
);
const openAgentPanel = agent => {
  selectedAgentId.value = agent.id;
};
const closeAgentPanel = () => {
  selectedAgentId.value = null;
};

onMounted(() => {
  store.dispatch('agents/get');
  store.dispatch('customRole/getCustomRole');
  store.dispatch('teams/get');
});

const findCustomRole = agent =>
  customRoles.value.find(role => role.id === agent.custom_role_id);

const getAgentRoleName = agent => {
  if (!agent.custom_role_id) {
    return t(`AGENT_MGMT.AGENT_TYPES.${agent.role.toUpperCase()}`);
  }
  const customRole = findCustomRole(agent);
  return customRole ? customRole.name : '';
};

const getAgentRolePermissions = agent => {
  if (!agent.custom_role_id) {
    return [];
  }
  const customRole = findCustomRole(agent);
  return customRole?.permissions || [];
};

const verifiedAdministrators = computed(() => {
  return agentList.value.filter(
    agent => agent.role === 'administrator' && agent.confirmed
  );
});

// "Added on <date> by <name>" from the membership row (inviter + created_at).
const addedLine = agent => {
  const date = new Date(agent.added_at).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
  return agent.added_by_name
    ? t('AGENT_MGMT.LIST.ADDED_BY', { date, name: agent.added_by_name })
    : t('AGENT_MGMT.LIST.ADDED_ON', { date });
};

const showEditAction = agent => {
  return currentUserId.value !== agent.id;
};

const showDeleteAction = agent => {
  if (currentUserId.value === agent.id) {
    return false;
  }

  if (!agent.confirmed) {
    return true;
  }

  if (agent.role === 'administrator') {
    return verifiedAdministrators.value.length !== 1;
  }
  return true;
};

const showAlertMessage = message => {
  loading.value[currentAgent.value.id] = false;
  currentAgent.value = {};
  agentAPI.value.message = message;
  useAlert(message);
};

const openAddPopup = () => {
  showAddPopup.value = true;
};
const hideAddPopup = () => {
  showAddPopup.value = false;
};

const openEditPopup = agent => {
  showEditPopup.value = true;
  currentAgent.value = agent;
};
const hideEditPopup = () => {
  showEditPopup.value = false;
};
const editSelectedAgent = () => {
  if (selectedAgent.value) openEditPopup(selectedAgent.value);
};

const openDeletePopup = agent => {
  showDeletePopup.value = true;
  currentAgent.value = agent;
};
const closeDeletePopup = () => {
  showDeletePopup.value = false;
};

const deleteAgent = async id => {
  try {
    await store.dispatch('agents/delete', id);
    showAlertMessage(t('AGENT_MGMT.DELETE.API.SUCCESS_MESSAGE'));
  } catch (error) {
    showAlertMessage(t('AGENT_MGMT.DELETE.API.ERROR_MESSAGE'));
  }
};
const confirmDeletion = () => {
  loading.value[currentAgent.value.id] = true;
  closeDeletePopup();
  deleteAgent(currentAgent.value.id);
};
</script>

<template>
  <SettingsLayout
    :is-loading="uiFlags.isFetching"
    :loading-message="$t('AGENT_MGMT.LOADING')"
    :no-records-found="!agentList.length"
    :no-records-message="$t('AGENT_MGMT.LIST.404')"
  >
    <template #header>
      <BaseSettingsHeader
        v-model:search-query="searchQuery"
        :title="$t('AGENT_MGMT.HEADER')"
        :description="$t('AGENT_MGMT.DESCRIPTION')"
        :link-text="$t('AGENT_MGMT.LEARN_MORE')"
        :search-placeholder="$t('AGENT_MGMT.SEARCH_PLACEHOLDER')"
        feature-name="agents"
      >
        <template v-if="agentList?.length" #count>
          <span class="text-body-main text-n-slate-11">
            {{ $t('AGENT_MGMT.COUNT', { n: agentList.length }) }}
          </span>
        </template>
        <template #tabs>
          <div class="flex items-center gap-2 flex-wrap">
            <select
              v-model="roleFilter"
              class="agent-filter"
              :aria-label="$t('AGENT_MGMT.FILTERS.ROLE')"
            >
              <option value="">{{ $t('AGENT_MGMT.FILTERS.ROLE') }}</option>
              <option
                v-for="option in roleOptions"
                :key="option.value"
                :value="option.value"
              >
                {{ option.label }}
              </option>
            </select>
            <select
              v-model="statusFilter"
              class="agent-filter"
              :aria-label="$t('AGENT_MGMT.FILTERS.STATUS')"
            >
              <option value="">{{ $t('AGENT_MGMT.FILTERS.STATUS') }}</option>
              <option
                v-for="option in statusOptions"
                :key="option.value"
                :value="option.value"
              >
                {{ option.label }}
              </option>
            </select>
            <select
              v-model="inboxFilter"
              class="agent-filter"
              :aria-label="$t('AGENT_MGMT.FILTERS.INBOX')"
            >
              <option value="">{{ $t('AGENT_MGMT.FILTERS.INBOX') }}</option>
              <option
                v-for="inbox in inboxes"
                :key="inbox.id"
                :value="inbox.id"
              >
                {{ inbox.name }}
              </option>
            </select>
            <Button
              v-if="hasActiveFilters"
              ghost
              slate
              xs
              icon="i-lucide-x"
              :label="$t('AGENT_MGMT.FILTERS.CLEAR')"
              @click="clearFilters"
            />
          </div>
        </template>
        <template #actions>
          <Button
            v-if="canViewTeamPresence"
            :label="$t('TELETEL_TEAM_PRESENCE.HEADER')"
            icon="i-lucide-radio-tower"
            size="sm"
            variant="faded"
            color="slate"
            @click="openTeamPresence"
          />
          <Button
            :label="$t('AGENT_MGMT.HEADER_BTN_TXT')"
            size="sm"
            @click="openAddPopup"
          />
        </template>
      </BaseSettingsHeader>
    </template>
    <template #body>
      <div class="flex w-full items-stretch gap-0">
        <div class="min-w-0 flex-1">
          <span
            v-if="
              !filteredAgentList.length && (searchQuery || hasActiveFilters)
            "
            class="flex-1 flex items-center justify-center py-20 text-center text-body-main !text-base text-n-slate-11"
          >
            {{ $t('AGENT_MGMT.NO_RESULTS') }}
          </span>
          <div v-else class="divide-y divide-n-weak border-t border-n-weak">
            <div
              v-for="(agent, index) in filteredAgentList"
              :key="agent.email"
              class="flex justify-between flex-row items-start gap-4 py-4 px-2 -mx-2 rounded-lg cursor-pointer hover:bg-n-alpha-1 transition-colors"
              :class="{ 'bg-n-alpha-2': selectedAgentId === agent.id }"
              role="button"
              tabindex="0"
              @click="openAgentPanel(agent)"
              @keydown.enter="openAgentPanel(agent)"
            >
              <div class="flex items-center gap-4">
                <Avatar
                  :src="agent.thumbnail"
                  :name="agent.name"
                  :status="agent.availability_status"
                  :size="40"
                  hide-offline-status
                />
                <div class="flex flex-col gap-1.5 items-start">
                  <span class="block text-heading-3 text-n-slate-12 capitalize">
                    {{ agent.name }}
                  </span>
                  <div class="flex items-center gap-2">
                    <span class="text-body-main text-n-slate-11">
                      {{ agent.email }}
                    </span>
                    <div class="w-px h-3 bg-n-strong rounded-lg" />
                    <span
                      class="block w-fit text-body-main text-n-slate-11 relative"
                      :class="{
                        'hover:text-n-slate-12 group cursor-pointer':
                          agent.custom_role_id,
                      }"
                    >
                      {{ getAgentRoleName(agent) }}

                      <div
                        class="absolute ltr:left-0 rtl:right-0 z-10 hidden w-[300px] bg-n-alpha-3 backdrop-blur-[100px] rounded-xl outline outline-1 outline-n-container shadow-lg top-14 md:top-12"
                        :class="{ 'group-hover:block': agent.custom_role_id }"
                      >
                        <div class="flex flex-col gap-1 p-4">
                          <span class="text-heading-3 text-n-slate-12">
                            {{ $t('AGENT_MGMT.LIST.AVAILABLE_CUSTOM_ROLE') }}
                          </span>
                          <ul class="ltr:pl-4 rtl:pr-4 mb-0 list-disc">
                            <li
                              v-for="permission in getAgentRolePermissions(
                                agent
                              )"
                              :key="permission"
                              class="text-body-main text-n-slate-11"
                            >
                              {{
                                $t(
                                  `CUSTOM_ROLE.PERMISSIONS.${permission.toUpperCase()}`
                                )
                              }}
                            </li>
                          </ul>
                        </div>
                      </div>
                    </span>
                    <div class="w-px h-3 bg-n-strong rounded-lg" />
                    <span
                      v-if="agent.confirmed"
                      class="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-n-teal-3 text-n-teal-11"
                      :title="$t('AGENT_MGMT.LIST.VERIFIED')"
                    >
                      <Icon icon="i-lucide-check" class="size-3" />
                      {{ $t('AGENT_MGMT.LIST.VERIFIED') }}
                    </span>
                    <span
                      v-else
                      class="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-n-amber-3 text-n-amber-11"
                      :title="$t('AGENT_MGMT.LIST.VERIFICATION_PENDING')"
                    >
                      <Icon icon="i-lucide-alert-circle" class="size-3" />
                      {{ $t('AGENT_MGMT.LIST.VERIFICATION_PENDING') }}
                    </span>
                    <span
                      v-if="agent.role === 'administrator'"
                      class="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-n-blue-3 text-n-blue-11"
                    >
                      <Icon icon="i-lucide-shield-check" class="size-3" />
                      {{ $t('AGENT_MGMT.AGENT_TYPES.ADMINISTRATOR') }}
                    </span>
                  </div>
                  <span
                    v-if="agent.added_at"
                    class="text-xs text-n-slate-10 tabular-nums"
                  >
                    {{ addedLine(agent) }}
                  </span>
                  <div
                    v-if="
                      agentInboxes(agent).length || agentTeams(agent).length
                    "
                    class="flex flex-wrap items-center gap-1"
                  >
                    <span
                      v-for="name in agentInboxes(agent)"
                      :key="`inbox-${name}`"
                      class="inline-flex items-center gap-1 px-1.5 py-0.5 text-xs rounded-md bg-n-alpha-2 text-n-slate-11"
                      :title="$t('AGENT_MGMT.FILTERS.INBOX')"
                    >
                      <Icon icon="i-lucide-inbox" class="size-3" />
                      {{ name }}
                    </span>
                    <span
                      v-for="name in agentTeams(agent)"
                      :key="`team-${name}`"
                      class="inline-flex items-center gap-1 px-1.5 py-0.5 text-xs rounded-md bg-n-alpha-2 text-n-slate-11"
                      :title="$t('AGENT_MGMT.PANEL.TEAMS')"
                    >
                      <Icon icon="i-lucide-users" class="size-3" />
                      {{ name }}
                    </span>
                  </div>
                </div>
              </div>
              <div class="flex justify-end gap-3" @click.stop>
                <Button
                  v-if="showEditAction(agent)"
                  v-tooltip.top="$t('AGENT_MGMT.EDIT.BUTTON_TEXT')"
                  icon="i-woot-edit-pen"
                  slate
                  sm
                  @click="openEditPopup(agent)"
                />
                <Button
                  v-if="showDeleteAction(agent)"
                  v-tooltip.top="$t('AGENT_MGMT.DELETE.BUTTON_TEXT')"
                  icon="i-woot-bin"
                  slate
                  sm
                  class="hover:enabled:text-n-ruby-11 hover:enabled:bg-n-ruby-2"
                  :is-loading="loading[agent.id]"
                  @click="openDeletePopup(agent, index)"
                />
              </div>
            </div>
          </div>
        </div>

        <!-- Agent side panel: same header as every side panel in the app -->
        <aside
          v-if="selectedAgent"
          class="hidden md:flex w-[22rem] shrink-0 flex-col ltr:ml-4 rtl:mr-4 rounded-xl border border-n-weak bg-n-solid-1 overflow-hidden self-start sticky top-2 max-h-[calc(100vh-6rem)]"
        >
          <SidebarActionsHeader
            :title="selectedAgent.name"
            :buttons="
              showEditAction(selectedAgent)
                ? [
                    {
                      key: 'edit',
                      icon: 'i-woot-edit-pen',
                      tooltip: $t('AGENT_MGMT.EDIT.BUTTON_TEXT'),
                    },
                  ]
                : []
            "
            @click="editSelectedAgent"
            @close="closeAgentPanel"
          />
          <div class="min-h-0 flex-1 overflow-auto p-4 flex flex-col gap-4">
            <div class="flex items-center gap-3">
              <Avatar
                :src="selectedAgent.thumbnail"
                :name="selectedAgent.name"
                :status="selectedAgent.availability_status"
                :size="48"
              />
              <div class="min-w-0 flex flex-col">
                <span class="text-heading-3 text-n-slate-12 truncate">
                  {{ selectedAgent.name }}
                </span>
                <span class="text-sm text-n-slate-11 truncate">
                  {{ selectedAgent.email }}
                </span>
              </div>
            </div>
            <dl class="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-sm">
              <dt class="text-n-slate-11">
                {{ $t('AGENT_MGMT.PANEL.ROLE') }}
              </dt>
              <dd class="text-n-slate-12">
                {{ getAgentRoleName(selectedAgent) }}
              </dd>
              <dt class="text-n-slate-11">
                {{ $t('AGENT_MGMT.PANEL.AVAILABILITY') }}
              </dt>
              <dd class="text-n-slate-12">
                {{
                  $t(
                    `AGENT_MGMT.FILTERS.STATUS_${(selectedAgent.availability_status || 'offline').toUpperCase()}`
                  )
                }}
              </dd>
              <dt class="text-n-slate-11">
                {{ $t('AGENT_MGMT.LIST.STATUS') }}
              </dt>
              <dd class="text-n-slate-12">
                {{
                  selectedAgent.confirmed
                    ? $t('AGENT_MGMT.LIST.VERIFIED')
                    : $t('AGENT_MGMT.LIST.VERIFICATION_PENDING')
                }}
              </dd>
              <template v-if="selectedAgent.added_at">
                <dt class="text-n-slate-11">
                  {{ $t('AGENT_MGMT.PANEL.ADDED') }}
                </dt>
                <dd class="text-n-slate-12">{{ addedLine(selectedAgent) }}</dd>
              </template>
            </dl>
            <div>
              <span class="block text-xs font-medium text-n-slate-11 mb-1">
                {{ $t('AGENT_MGMT.PANEL.INBOXES') }}
              </span>
              <div
                v-if="agentInboxes(selectedAgent).length"
                class="flex flex-wrap gap-1"
              >
                <span
                  v-for="name in agentInboxes(selectedAgent)"
                  :key="`p-inbox-${name}`"
                  class="inline-flex items-center gap-1 px-1.5 py-0.5 text-xs rounded-md bg-n-alpha-2 text-n-slate-12"
                >
                  <Icon icon="i-lucide-inbox" class="size-3" />
                  {{ name }}
                </span>
              </div>
              <span v-else class="text-sm text-n-slate-10">
                {{ $t('AGENT_MGMT.PANEL.NONE') }}
              </span>
            </div>
            <div>
              <span class="block text-xs font-medium text-n-slate-11 mb-1">
                {{ $t('AGENT_MGMT.PANEL.TEAMS') }}
              </span>
              <div
                v-if="agentTeams(selectedAgent).length"
                class="flex flex-wrap gap-1"
              >
                <span
                  v-for="name in agentTeams(selectedAgent)"
                  :key="`p-team-${name}`"
                  class="inline-flex items-center gap-1 px-1.5 py-0.5 text-xs rounded-md bg-n-alpha-2 text-n-slate-12"
                >
                  <Icon icon="i-lucide-users" class="size-3" />
                  {{ name }}
                </span>
              </div>
              <span v-else class="text-sm text-n-slate-10">
                {{ $t('AGENT_MGMT.PANEL.NONE') }}
              </span>
            </div>
            <div v-if="getAgentRolePermissions(selectedAgent).length">
              <span class="block text-xs font-medium text-n-slate-11 mb-1">
                {{ $t('AGENT_MGMT.LIST.AVAILABLE_CUSTOM_ROLE') }}
              </span>
              <ul
                class="ltr:pl-4 rtl:pr-4 mb-0 list-disc text-sm text-n-slate-11"
              >
                <li
                  v-for="permission in getAgentRolePermissions(selectedAgent)"
                  :key="permission"
                >
                  {{
                    $t(`CUSTOM_ROLE.PERMISSIONS.${permission.toUpperCase()}`)
                  }}
                </li>
              </ul>
            </div>
            <div class="flex gap-2 pt-2 border-t border-n-weak">
              <Button
                v-if="showEditAction(selectedAgent)"
                slate
                sm
                icon="i-woot-edit-pen"
                :label="$t('AGENT_MGMT.EDIT.BUTTON_TEXT')"
                @click="editSelectedAgent"
              />
              <Button
                v-if="showDeleteAction(selectedAgent)"
                slate
                sm
                icon="i-woot-bin"
                class="hover:enabled:text-n-ruby-11 hover:enabled:bg-n-ruby-2"
                :label="$t('AGENT_MGMT.DELETE.BUTTON_TEXT')"
                @click="openDeletePopup(selectedAgent)"
              />
            </div>
          </div>
        </aside>
      </div>
    </template>

    <woot-modal v-model:show="showAddPopup" :on-close="hideAddPopup">
      <AddAgent @close="hideAddPopup" />
    </woot-modal>

    <woot-modal v-model:show="showEditPopup" :on-close="hideEditPopup">
      <EditAgent
        v-if="showEditPopup"
        :id="currentAgent.id"
        :name="currentAgent.name"
        :provider="currentAgent.provider"
        :type="currentAgent.role"
        :email="currentAgent.email"
        :availability="currentAgent.availability_status"
        :custom-role-id="currentAgent.custom_role_id"
        @close="hideEditPopup"
      />
    </woot-modal>

    <woot-delete-modal
      v-model:show="showDeletePopup"
      :on-close="closeDeletePopup"
      :on-confirm="confirmDeletion"
      :title="$t('AGENT_MGMT.DELETE.CONFIRM.TITLE')"
      :message="$t('AGENT_MGMT.DELETE.CONFIRM.MESSAGE')"
      :message-value="deleteMessage"
      :confirm-text="deleteConfirmText"
      :reject-text="deleteRejectText"
    />
  </SettingsLayout>
</template>

<style scoped>
.agent-filter {
  @apply h-8 w-auto min-w-[8rem] mb-0 py-0 ltr:pr-7 rtl:pl-7 text-xs rounded-lg border border-n-weak bg-n-solid-1 text-n-slate-12;
}
</style>
